/**
 * Repair a folder list whose parent pointers no longer form a tree, by cutting
 * the broken links so every folder is reachable from the root again.
 *
 * Corrupt parent graphs reach clients in the wild (deletes without cascade,
 * unvalidated moves — historically via Professor Ka's store functions). The
 * web sidebar tolerates them by dumping decks of unreachable folders at its
 * root, but the web dashboard and the mobile folder browse render unreachable
 * folders NOWHERE — whole subtrees of healthy decks silently vanish — and
 * parent-chain walks (breadcrumbs, folder study views) never terminate on a
 * cycle. Both platforms normalize their live folder list through this before
 * rendering, so every reader sees the same, safe tree.
 *
 * Cuts (parentFolder → undefined, i.e. root) exactly:
 * - folders whose parent id doesn't exist in the (live) list, incl. self-parents;
 * - ONE member per parent cycle (the lowest id, for determinism) — the rest of
 *   the cycle stays intact as that member's now-rooted subtree.
 *
 * Pass the list already filtered to live folders (no `deletedTimeISO`) — a
 * pointer at a soft-deleted parent counts as dangling and gets cut.
 *
 * Returns the input array unchanged (same reference) when nothing is broken,
 * so memoized consumers don't re-render.
 */

/** Minimal folder shape the normalizer needs — works for any platform's row type. */
export type FolderParentRef = { id: string; parentFolder?: string | null };

export function normalizeFolderParents<T extends FolderParentRef>(folders: T[]): T[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const cutIds = new Set<string>();

  for (const f of folders) {
    const parent = f.parentFolder;
    if (!parent || parent === 'unassigned') continue;
    if (parent === f.id || !byId.has(parent)) cutIds.add(f.id);
  }

  // Roots (or folders whose chain ends at an already-cut link) are safe.
  // Walk up from every folder; a repeat inside the current path is a cycle.
  const safe = new Set<string>();
  for (const f of folders) {
    const path: string[] = [];
    const onPath = new Set<string>();
    let current: T | undefined = f;
    while (current) {
      if (safe.has(current.id) || cutIds.has(current.id)) break;
      if (onPath.has(current.id)) {
        const members = path.slice(path.indexOf(current.id));
        cutIds.add(members.reduce((a, b) => (a < b ? a : b)));
        break;
      }
      onPath.add(current.id);
      path.push(current.id);
      const parent: string | null | undefined = current.parentFolder;
      if (!parent || parent === 'unassigned') break;
      current = byId.get(parent);
    }
    // Every folder on the walked path now terminates at a root or a cut link.
    for (const id of path) safe.add(id);
  }

  if (cutIds.size === 0) return folders;
  return folders.map((f) =>
    // Overriding one property of a spread T is safe here: the cut only widens
    // `parentFolder` back to the optional/undefined the constraint allows.
    cutIds.has(f.id) ? ({ ...f, parentFolder: undefined } as T) : f,
  );
}

/**
 * The ids of every ancestor of `folderId`, nearest parent first.
 *
 * This is the predicate behind "reveal the active folder in the sidebar": a
 * tree that hides the active row inside collapsed ancestors shows no highlight
 * at all, so both platforms expand this chain on navigation. It lives in shared
 * because it is a rule about the folder GRAPH, not about either sidebar — mobile
 * walks it to expand its rail rows, web to expand its dnd tree, and a drifting
 * second copy is exactly how the two ended up disagreeing before.
 *
 * Callers pass their already-normalized, live list (see
 * {@link normalizeFolderParents}), which is what makes the walk terminate: the
 * normalizer has cut every cycle to root. The `seen` guard below is belt-and-
 * braces for a caller that forgets, so a corrupt graph degrades to a short
 * chain instead of hanging the render it is called from.
 *
 * Returns `[]` for a root folder, an unknown id, or `null` — all three mean
 * "nothing to expand", which is what the callers want.
 */
export function folderAncestorIds<T extends FolderParentRef>(
  folders: T[],
  folderId: string | null | undefined,
): string[] {
  if (!folderId) return [];
  const parentById = new Map<string, string | null>(
    folders.map((f) => [f.id, f.parentFolder ?? null]),
  );
  const chain: string[] = [];
  const seen = new Set<string>([folderId]);
  let cursor = parentById.get(folderId) ?? null;
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    chain.push(cursor);
    cursor = parentById.get(cursor) ?? null;
  }
  return chain;
}

/**
 * `prev` plus every ancestor of the active folder — the "reveal the active row"
 * rule both sidebars run on navigation.
 *
 * Shared because the RULE is what must not drift, not just the walk:
 * - ancestors are only ever ADDED, never removed, so a folder the user
 *   deliberately collapsed stays collapsed unless the active row is inside it;
 * - when there is nothing new to add it returns `prev` UNCHANGED (same
 *   reference), so a caller can drive it straight from a state setter without
 *   causing a render on every unrelated folder mutation.
 *
 * Each platform still owns its own expansion STATE and its own reveal mechanism
 * (mobile measures and scrolls the rail; web scrolls its virtualized list) —
 * only this decision is common.
 */
export function withFolderAncestorsExpanded<T extends FolderParentRef>(
  prev: ReadonlySet<string>,
  folders: T[],
  activeFolderId: string | null | undefined,
  options?: {
    /**
     * Also expand `activeFolderId` ITSELF, not just its ancestors.
     *
     * Two callers, two meanings. When the active thing IS a folder, ancestors
     * alone make its row visible — expanding it too would force its children
     * open, overriding a collapse the user chose. When the active thing is a
     * DECK, the id passed here is the deck's containing folder, and the deck's
     * row lives INSIDE it — so the folder itself must open or the ancestors
     * were expanded for nothing.
     */
    includeSelf?: boolean;
  },
): ReadonlySet<string> {
  const chain = folderAncestorIds(folders, activeFolderId);
  if (options?.includeSelf && activeFolderId) {
    // Only a folder that exists — a dangling id would "expand" a row that is
    // not in the tree, and the empty-chain fast path below would stop firing.
    if (folders.some((f) => f.id === activeFolderId)) chain.unshift(activeFolderId);
  }
  if (chain.length === 0 || chain.every((id) => prev.has(id))) return prev;
  const next = new Set(prev);
  for (const id of chain) next.add(id);
  return next;
}

/** Folder shape the hidden-subtree walk needs. */
export type FolderArchiveRef = FolderParentRef & { archivedTimeISO?: string | null };

/**
 * The moment `archivedTimeISO` on a folder started to mean "Hide folder" — the
 * merge of #752 to main (2026-10-15). Before it, the same field meant only
 * "Hide from To-Do": the web To-Do widget stamped it (until v3.16.0) and
 * Supabase-era rows carried it into UserDo verbatim, while the web dashboard
 * and sidebar kept rendering those folders. Reading those stamps as "hidden"
 * made whole libraries vanish on web the day the hide feature shipped.
 *
 * So a stamp from before this instant is a legacy To-Do flag, and does NOT
 * hide the folder. Every reader goes through {@link isFolderHidden}, and every
 * writer through {@link folderHideStampISO}, so a fresh hide always lands after
 * it. Read-time rather than a data migration: nothing in a UserDo is rewritten,
 * and old clients that still read the raw field keep rendering what they
 * always did.
 */
export const FOLDER_HIDE_EPOCH_ISO = '2026-10-15T03:50:09.000Z';
const FOLDER_HIDE_EPOCH_MS = Date.parse(FOLDER_HIDE_EPOCH_ISO);

/**
 * Whether the user hid THIS folder (not its ancestors — see
 * {@link hiddenFolderIds} for the subtree). A stamp before
 * {@link FOLDER_HIDE_EPOCH_ISO}, or one that is not a parseable date (only
 * legacy rows carry those — the hide writes `toISOString()`), is not a hide.
 */
export function isFolderHidden(folder: { archivedTimeISO?: string | null }): boolean {
  const stamp = folder.archivedTimeISO;
  if (!stamp) return false;
  const at = Date.parse(stamp);
  return Number.isFinite(at) && at >= FOLDER_HIDE_EPOCH_MS;
}

/**
 * The `archivedTimeISO` a folder hide writes: now, but never before
 * {@link FOLDER_HIDE_EPOCH_ISO} — a device whose clock runs behind would
 * otherwise write a hide that {@link isFolderHidden} ignores.
 */
export function folderHideStampISO(now: number = Date.now()): string {
  return new Date(Math.max(now, FOLDER_HIDE_EPOCH_MS)).toISOString();
}

/** Shared "nothing is hidden" result — see {@link hiddenFolderIds}. */
const NO_HIDDEN_FOLDERS: ReadonlySet<string> = new Set<string>();

/**
 * The ids of every folder the user has HIDDEN — each explicitly-hidden folder
 * plus everything beneath it.
 *
 * The descendant half is the actual product rule, and it is the reason this
 * lives in shared instead of being a `filter` at each call site. Hiding a
 * folder hides what is IN it: hide "Semester 1" and its sub-folders and their
 * decks go with it. Filtering only the explicitly-hidden row instead leaves
 * every child pointing at a parent that is no longer in the tree, and both
 * clients' trees re-root an orphan — so hiding one folder would SCATTER its
 * children across the root of the library, which is the opposite of what the
 * user asked for.
 *
 * Feed the result to folders AND to decks: a deck whose folder is hidden is
 * hidden too, for exactly the same reason.
 *
 * Pass the live list (no `deletedTimeISO`). Order against
 * {@link normalizeFolderParents} does not matter — the `seen` guard below
 * terminates on a cycle the normalizer has not cut yet — but normalizing first
 * is preferred, so a hidden folder stranded inside a cycle still hides.
 */
export function hiddenFolderIds<T extends FolderArchiveRef>(
  folders: readonly T[],
): ReadonlySet<string> {
  const hidden = new Set<string>();
  for (const f of folders) {
    if (isFolderHidden(f)) hidden.add(f.id);
  }
  // Nothing hidden is the overwhelmingly common case, so skip the walk AND
  // hand back one shared instance — a fresh `Set` per call would make every
  // consumer's memo produce a new reference on each render.
  if (hidden.size === 0) return NO_HIDDEN_FOLDERS;

  const byId = new Map(folders.map((f) => [f.id, f]));
  for (const f of folders) {
    if (hidden.has(f.id)) continue;
    const path: string[] = [];
    const seen = new Set<string>();
    let current: T | undefined = f;
    while (current) {
      if (seen.has(current.id)) break; // cycle — leave it visible
      seen.add(current.id);
      if (hidden.has(current.id)) {
        for (const id of path) hidden.add(id);
        break;
      }
      path.push(current.id);
      const parent: string | null | undefined = current.parentFolder;
      if (!parent || parent === 'unassigned') break;
      current = byId.get(parent);
    }
  }
  return hidden;
}

/**
 * The folders a library surface should render — live folders minus every
 * hidden subtree. See {@link hiddenFolderIds}.
 *
 * Returns the input array unchanged (same reference) when nothing is hidden,
 * so memoized consumers don't re-render.
 */
export function selectVisibleFolders<T extends FolderArchiveRef>(folders: T[]): T[] {
  const hidden = hiddenFolderIds(folders);
  if (hidden.size === 0) return folders;
  return folders.filter((f) => !hidden.has(f.id));
}

/**
 * How many decks each HIDDEN ROOT folder is holding — the number a "restore
 * this folder" row should show, since restoring it brings the whole subtree
 * back.
 *
 * Keyed by the TOP-MOST hidden ancestor, not by the deck's own folder: a deck
 * two levels down inside a hidden tree belongs to the row the user can
 * actually act on. A nested folder that was ALSO hidden in its own right still
 * gets its own (empty) row from the caller — restoring it alone would reveal
 * nothing while its parent stays hidden, and the count says so.
 *
 * Shared because both Archive surfaces render the same number from the same
 * two lists, and a hand-copied graph walk on each client is how they drift —
 * the first pair already had: one counted study-group mirror decks and the
 * other could not see them.
 *
 * Pass the LIVE lists: live folders, and decks that are neither deleted nor
 * study-group mirrors. This counts what it is given.
 */
export function deckCountByHiddenRoot<
  T extends FolderArchiveRef,
  D extends { folder?: string | null },
>(folders: readonly T[], decks: readonly D[]): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  const hidden = hiddenFolderIds(folders);
  if (hidden.size === 0) return counts;

  const parentById = new Map<string, string | null>(
    folders.map((f) => [f.id, f.parentFolder ?? null]),
  );
  for (const deck of decks) {
    const folderId = deck.folder;
    if (!folderId || !hidden.has(folderId)) continue;
    // Walk up while the parent is ALSO hidden; where that stops is the folder
    // whose row owns this deck. `seen` guards a cycle the normalizer has not
    // cut, so a corrupt graph cannot hang the render this is called from.
    let owner = folderId;
    const seen = new Set<string>([owner]);
    let parent = parentById.get(owner) ?? null;
    while (parent && hidden.has(parent) && !seen.has(parent)) {
      owner = parent;
      seen.add(owner);
      parent = parentById.get(owner) ?? null;
    }
    counts.set(owner, (counts.get(owner) ?? 0) + 1);
  }
  return counts;
}
